import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Shield, Lock, Eye, Server, AlertTriangle, CheckCircle, FileText, Users } from 'lucide-react';

export function SecurityPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="security-page">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E8F9EE] to-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#00D95F] rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Security at GiriChat</h1>
          <p className="text-xl text-gray-600">
            Your security and privacy are our top priorities. Learn about the measures we take to protect your data.
          </p>
        </div>
      </div>

      <div className="flex-1 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Trust Badges */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E8F9EE] rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="w-8 h-8 text-[#00D95F]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">SOC 2 Type II</h3>
              <p className="text-sm text-gray-600">Certified</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E8F9EE] rounded-full flex items-center justify-center mx-auto mb-3">
                <Lock className="w-8 h-8 text-[#00D95F]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">ISO 27001</h3>
              <p className="text-sm text-gray-600">Compliant</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E8F9EE] rounded-full flex items-center justify-center mx-auto mb-3">
                <FileText className="w-8 h-8 text-[#00D95F]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">GDPR</h3>
              <p className="text-sm text-gray-600">Compliant</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#E8F9EE] rounded-full flex items-center justify-center mx-auto mb-3">
                <Server className="w-8 h-8 text-[#00D95F]" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">99.9%</h3>
              <p className="text-sm text-gray-600">Uptime SLA</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Commitment to Security</h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                At GiriChat, security isn't just a feature—it's the foundation of everything we do. We employ industry-leading security practices and continuously invest in protecting your data and ensuring the reliability of our platform.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our security program is designed to protect your information through multiple layers of defense, regular security assessments, and compliance with international security standards.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Lock className="w-8 h-8 text-[#00D95F]" />
                Data Encryption
              </h2>
              
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-[#00D95F] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Encryption in Transit</h3>
                      <p className="text-gray-700 mb-3">
                        All data transmitted between your browser and our servers is encrypted using TLS 1.3 (Transport Layer Security), the same encryption standard used by banks and financial institutions.
                      </p>
                      <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm">
                        <li>256-bit SSL/TLS encryption</li>
                        <li>Perfect Forward Secrecy (PFS)</li>
                        <li>HTTPS enforced on all pages</li>
                        <li>Certificate pinning for mobile apps</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-[#00D95F] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Encryption at Rest</h3>
                      <p className="text-gray-700 mb-3">
                        All data stored in our databases and backup systems is encrypted using AES-256 encryption, ensuring your data remains protected even if physical storage is compromised.
                      </p>
                      <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm">
                        <li>AES-256 encryption for all databases</li>
                        <li>Encrypted backups and snapshots</li>
                        <li>Encrypted file storage</li>
                        <li>Hardware Security Modules (HSM) for key management</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Server className="w-8 h-8 text-[#00D95F]" />
                Infrastructure Security
              </h2>
              
              <div className="space-y-4">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Cloud Infrastructure</h3>
                  <p className="text-gray-700 mb-3">
                    Our infrastructure is hosted on Amazon Web Services (AWS), a SOC 1, SOC 2, and ISO 27001 certified cloud provider with world-class security controls.
                  </p>
                  <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm">
                    <li>Multi-region redundancy for high availability</li>
                    <li>Automated failover and disaster recovery</li>
                    <li>DDoS protection and traffic filtering</li>
                    <li>Regular security patches and updates</li>
                  </ul>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Network Security</h3>
                  <p className="text-gray-700 mb-3">
                    We implement multiple layers of network security to protect against unauthorized access and attacks.
                  </p>
                  <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm">
                    <li>Firewall protection and intrusion detection</li>
                    <li>Network segmentation and isolation</li>
                    <li>Rate limiting and API throttling</li>
                    <li>Web Application Firewall (WAF)</li>
                  </ul>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Data Centers</h3>
                  <p className="text-gray-700 mb-3">
                    Our data is stored in secure, certified data centers with physical security controls.
                  </p>
                  <ul className="list-disc pl-5 text-gray-700 space-y-1 text-sm">
                    <li>24/7 surveillance and monitoring</li>
                    <li>Biometric access controls</li>
                    <li>Environmental controls (fire, flood, temperature)</li>
                    <li>Redundant power and cooling systems</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Eye className="w-8 h-8 text-[#00D95F]" />
                Access Controls
              </h2>
              
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Authentication & Authorization</h3>
                <p className="text-gray-700 mb-4">
                  We implement strict access controls to ensure only authorized users can access your data.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">User Access</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>✓ Strong password requirements</li>
                      <li>✓ Two-factor authentication (2FA)</li>
                      <li>✓ Single Sign-On (SSO) support</li>
                      <li>✓ Session timeout controls</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Employee Access</h4>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li>✓ Principle of least privilege</li>
                      <li>✓ Role-based access control (RBAC)</li>
                      <li>✓ Mandatory 2FA for all employees</li>
                      <li>✓ Access logging and auditing</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-[#00D95F]" />
                Security Monitoring
              </h2>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                We continuously monitor our systems for security threats and anomalies using advanced security tools and practices.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">24/7 Monitoring</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Real-time threat detection</li>
                    <li>• Automated security alerts</li>
                    <li>• Log aggregation and analysis</li>
                    <li>• Anomaly detection systems</li>
                  </ul>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Incident Response</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Dedicated security team</li>
                    <li>• Incident response plan</li>
                    <li>• Regular security drills</li>
                    <li>• Post-incident reviews</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <Users className="w-8 h-8 text-[#00D95F]" />
                Security Testing
              </h2>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                We regularly test our security controls to identify and address vulnerabilities before they can be exploited.
              </p>

              <div className="space-y-4">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Penetration Testing</h3>
                  <p className="text-gray-700 text-sm">
                    Independent security experts conduct quarterly penetration tests to identify vulnerabilities in our systems.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Vulnerability Scanning</h3>
                  <p className="text-gray-700 text-sm">
                    Automated vulnerability scans run continuously to detect known security issues in our infrastructure and applications.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Code Review</h3>
                  <p className="text-gray-700 text-sm">
                    All code changes undergo security review before deployment, and we use automated tools to detect security issues in our codebase.
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Bug Bounty Program</h3>
                  <p className="text-gray-700 text-sm">
                    We maintain a responsible disclosure program where security researchers can report vulnerabilities and receive rewards.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Compliance & Certifications</h2>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                We maintain compliance with industry standards and regulations to ensure the highest level of security and privacy protection.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#E8F9EE] border border-[#00D95F]/20 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">SOC 2 Type II</h3>
                  <p className="text-gray-700 text-sm">
                    Independently audited for security, availability, processing integrity, confidentiality, and privacy controls.
                  </p>
                </div>

                <div className="bg-[#E8F9EE] border border-[#00D95F]/20 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">ISO 27001</h3>
                  <p className="text-gray-700 text-sm">
                    Certified for our Information Security Management System (ISMS) following international best practices.
                  </p>
                </div>

                <div className="bg-[#E8F9EE] border border-[#00D95F]/20 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">GDPR</h3>
                  <p className="text-gray-700 text-sm">
                    Fully compliant with the European Union's General Data Protection Regulation for data privacy.
                  </p>
                </div>

                <div className="bg-[#E8F9EE] border border-[#00D95F]/20 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">CCPA</h3>
                  <p className="text-gray-700 text-sm">
                    Compliant with the California Consumer Privacy Act, ensuring transparency and control over personal data.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Responsible Disclosure</h2>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                If you discover a security vulnerability, please report it responsibly so we can address it quickly.
              </p>

              <div className="bg-gradient-to-br from-[#E8F9EE] to-[#D4F4E0] border border-[#00D95F]/20 rounded-lg p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Report a Security Issue</h3>
                <p className="text-gray-700 mb-4">
                  Please email us at: <a href="mailto:info@6miles.io" className="text-[#00D95F] hover:underline font-semibold">info@6miles.io</a>
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  We ask that you:
                </p>
                <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                  <li>Provide detailed information about the vulnerability</li>
                  <li>Give us reasonable time to address the issue before public disclosure</li>
                  <li>Do not access or modify user data without permission</li>
                  <li>Act in good faith and avoid privacy violations</li>
                </ul>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Questions?</h2>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                For questions about our security practices, please contact our security team:
              </p>
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <p className="text-gray-700 mb-2">
                  <strong>Email:</strong> <a href="mailto:info@6miles.io" className="text-[#00D95F] hover:underline">info@6miles.io</a>
                </p>
                <p className="text-gray-700">
                  <strong>PGP Key:</strong> Available upon request for encrypted communication
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
